import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Check, ArrowRight, Zap, X } from 'lucide-react';
import { PLANS, formatPrice, SubscriptionTier } from '@/lib/pricing';
import { PaymentModal } from '@/components/PaymentModal';
import { useAuth } from '@/contexts/AuthContext';
import logo from '@/assets/logo.png';
import { generateBreadcrumbSchema, generateFAQSchema, generatePricingSchema } from '@/lib/structuredData';

export default function Pricing() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('pro');

  const handleSelectPlan = (tier: SubscriptionTier) => {
    if (tier === 'free') {
      navigate('/auth');
      return;
    }
    
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Check if already on this or higher tier
    if (profile?.subscription === tier) {
      return;
    }
    
    setSelectedTier(tier);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    // Refresh the page to get updated profile
    window.location.reload();
  };

  const faqs = [
    {
      question: "Can I change plans later?",
      answer: "Yes! You can upgrade or downgrade at any time. When upgrading, you'll have immediate access to new features. When downgrading, changes take effect at the end of your billing period."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit/debit cards (Visa, Mastercard, Amex), UPI, net banking, and bank transfers for annual Business plans."
    },
    {
      question: "Is there a long-term contract?",
      answer: "No. All paid plans are month-to-month with no long-term commitment. Cancel anytime. Annual billing gives you 20% discount."
    },
    {
      question: "Do you offer discounts for annual billing?",
      answer: "Yes! Save 20% with annual billing on all paid plans. Starter: ₹4,790/year, Pro: ₹14,390/year, Business: ₹38,390/year."
    },
    {
      question: "What happens when I exceed my plan limits?",
      answer: "You'll receive a notification when approaching limits. Additional usage is queued until the next month or you upgrade. Your existing data remains safe."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. We use bank-level AES-256 encryption, row-level security policies, and regular security audits. Your data is never shared with third parties."
    }
  ];

  const getDisplayPrice = (plan: typeof PLANS[0]) => {
    if (plan.price === 0) return '₹0';
    return formatPrice(billingPeriod === 'annual' ? Math.round(plan.annualPrice / 12) : plan.price);
  };

  const comparisonFeatures = [
    { name: 'Transactions/month', free: '50', starter: '500', pro: 'Unlimited', business: 'Unlimited' },
    { name: 'Invoice OCR scanning', free: '5/month', starter: '50/month', pro: '200/month', business: 'Unlimited' },
    { name: 'AI Fraud Detection', free: false, starter: false, pro: true, business: true },
    { name: 'Cash Flow Forecasting', free: false, starter: false, pro: true, business: true },
    { name: 'Tally ERP Integration', free: false, starter: false, pro: true, business: true },
    { name: 'Custom Reports', free: false, starter: true, pro: true, business: true },
    { name: 'API Access', free: false, starter: false, pro: '1,000/day', business: 'Unlimited' },
    { name: 'Team Members', free: '1', starter: '1', pro: '3', business: '10' },
    { name: 'Data Retention', free: '3 days', starter: '30 days', pro: '90 days', business: 'Unlimited' },
    { name: 'Support Response', free: '72 hours', starter: '48 hours', pro: '4 hours', business: '1 hour' },
  ];

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://getsentinel.pro/' },
    { name: 'Pricing', url: 'https://getsentinel.pro/pricing' }
  ]);

  const faqSchema = generateFAQSchema(faqs);
  const productSchema = generatePricingSchema(PLANS.map(p => ({
    name: p.name,
    price: p.price,
    description: p.description,
    features: p.features
  })));

  return (
    <>
      <SEO
        title="Pricing - Transparent & Flexible Plans | Sentinel"
        description="Choose the perfect Sentinel plan for your business. Free tier for individuals. Starter at ₹499/mo, Pro at ₹1,499/mo for AI features. Business at ₹3,999/mo."
      />
      
      {/* Breadcrumb Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      
      {/* Product Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center mb-8 md:mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Simple, Transparent
                <span className="bg-gradient-primary bg-clip-text text-transparent"> Pricing</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Choose the plan that fits your business. No hidden fees. Cancel anytime.
              </p>
              
              {/* Billing Toggle */}
              <div className="inline-flex items-center gap-3 bg-surface p-1 rounded-full border border-border/50">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    billingPeriod === 'monthly' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                    billingPeriod === 'annual' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Annual
                  <span className="bg-success/20 text-success text-xs px-2 py-0.5 rounded-full">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
              {PLANS.map((plan, index) => (
                <Card 
                  key={index} 
                  className={`p-5 md:p-6 relative ${
                    plan.popular 
                      ? 'border-primary/50 shadow-card-hover lg:scale-105 z-10' 
                      : 'border-border/50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-primary px-3 py-1 rounded-full text-xs font-semibold text-white shadow-glow flex items-center gap-1">
                        <Zap size={12} /> Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="mb-5">
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl md:text-3xl font-bold">{getDisplayPrice(plan)}</span>
                      {plan.price > 0 && (
                        <span className="text-muted-foreground text-sm">/{billingPeriod === 'annual' ? 'mo' : plan.period.split(' ')[1]}</span>
                      )}
                      {plan.price === 0 && (
                        <span className="text-muted-foreground text-sm">/forever</span>
                      )}
                    </div>
                    {billingPeriod === 'annual' && plan.annualPrice > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {formatPrice(plan.annualPrice)} billed annually
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">{plan.description}</p>
                  </div>

                  <Button 
                    className="w-full mb-5" 
                    variant={plan.popular ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleSelectPlan(plan.tier)}
                    disabled={profile?.subscription === plan.tier}
                  >
                    {profile?.subscription === plan.tier ? 'Current Plan' : plan.cta}
                  </Button>

                  <div className="space-y-2">
                    {plan.features.slice(0, 7).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-xs">{feature}</span>
                      </div>
                    ))}
                    {plan.features.length > 7 && (
                      <p className="text-xs text-primary font-medium pl-6">
                        +{plan.features.length - 7} more features
                      </p>
                    )}
                    {plan.limitations.slice(0, 3).map((limitation, idx) => (
                      <div key={`limit-${idx}`} className="flex items-start gap-2 opacity-50">
                        <X className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-muted-foreground">{limitation}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>

            {/* Cost Breakdown Note */}
            <div className="max-w-2xl mx-auto mt-10 text-center">
              <p className="text-sm text-muted-foreground">
                All plans include: Secure cloud storage, automatic backups, and platform updates. 
                <br className="hidden md:block" />
                Average ROI: Prevent 1 duplicate payment to cover months of subscription cost.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="py-16 bg-surface/30">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Feature Comparison</h2>
            <div className="max-w-5xl mx-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-3 font-semibold">Feature</th>
                    <th className="text-center py-4 px-3 font-semibold">Free</th>
                    <th className="text-center py-4 px-3 font-semibold">Starter</th>
                    <th className="text-center py-4 px-3 font-semibold text-primary">Pro</th>
                    <th className="text-center py-4 px-3 font-semibold">Business</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {comparisonFeatures.map((feature, idx) => (
                    <tr key={idx}>
                      <td className="py-3 px-3">{feature.name}</td>
                      <td className="py-3 px-3 text-center">
                        {typeof feature.free === 'boolean' ? (
                          feature.free ? <Check className="w-4 h-4 mx-auto text-primary" /> : <X className="w-4 h-4 mx-auto text-muted-foreground" />
                        ) : feature.free}
                      </td>
                      <td className="py-3 px-3 text-center">
                        {typeof feature.starter === 'boolean' ? (
                          feature.starter ? <Check className="w-4 h-4 mx-auto text-primary" /> : <X className="w-4 h-4 mx-auto text-muted-foreground" />
                        ) : feature.starter}
                      </td>
                      <td className="py-3 px-3 text-center text-primary font-medium">
                        {typeof feature.pro === 'boolean' ? (
                          feature.pro ? <Check className="w-4 h-4 mx-auto text-primary" /> : <X className="w-4 h-4 mx-auto text-muted-foreground" />
                        ) : feature.pro}
                      </td>
                      <td className="py-3 px-3 text-center">
                        {typeof feature.business === 'boolean' ? (
                          feature.business ? <Check className="w-4 h-4 mx-auto text-primary" /> : <X className="w-4 h-4 mx-auto text-muted-foreground" />
                        ) : feature.business}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Pricing FAQs</h2>
            <div className="space-y-4 md:space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="p-5 md:p-6 border-border/50">
                  <h3 className="text-base md:text-lg font-semibold mb-2">{faq.question}</h3>
                  <p className="text-sm md:text-base text-muted-foreground">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6">
            <Card className="p-8 md:p-12 text-center bg-gradient-primary border-0">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Not sure which plan is right for you?
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-8">
                Schedule a demo with our team and we'll help you choose the best fit
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="shadow-card">
                  Schedule a Demo <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" onClick={() => navigate('/auth')}>
                  Start Free Trial
                </Button>
              </div>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border/50 py-8 bg-surface/30">
          <div className="container mx-auto px-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">&copy; 2025 SENTINEL. All rights reserved.</p>
            <p className="text-sm text-muted-foreground">
              Built by{' '}
              <a 
                href="https://bwestudios.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-bold text-foreground hover:text-primary transition-colors"
              >
                BWE Studio
              </a>
            </p>
          </div>
        </footer>

        {/* Payment Modal */}
        <PaymentModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          selectedTier={selectedTier}
          billingPeriod={billingPeriod}
          onSuccess={handlePaymentSuccess}
        />
      </div>
    </>
  );
}
